import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEventSubscribe, eventBus } from '../../events';
import { tourStepsFor } from '../../utils/onboardingTourSteps';
import { parseAvatar } from '../../utils/avatarProfile';
import { hasSeenTour, markTourSeen, isInitialFlyComplete } from '../../utils/onboardingFlags';
const BUBBLE_WIDTH = 300;
const BUBBLE_EST_HEIGHT = 200;
const MARGIN = 12;
const HIGHLIGHT_PAD = 6;
// Find a tour target by its data-tour attribute, ignoring elements that aren't
// actually laid out (collapsed/hidden) so we never anchor to a zero-size box.
const findTarget = (target) => {
    if (!target)
        return null;
    const el = document.querySelector(`[data-tour="${target}"]`);
    if (!el)
        return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0)
        return null;
    return el;
};
// A user-paced spotlight tour. Auto-runs once per user — but only for a
// signed-in, first-time user, and only AFTER the opening camera fly-to finishes.
// Replayable on demand from the Help hub. No external tour library.
export const OnboardingTour = () => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const [steps, setSteps] = useState([]);
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const evaluated = useRef(false);
    const armed = useRef(false); // true only for a user who should get the auto-tour
    const started = useRef(false);
    const avatar = parseAvatar(user === null || user === void 0 ? void 0 : user.avatar);
    const startTour = useCallback(() => {
        // Keep only steps whose target is present right now (centered steps always
        // qualify), so missing surfaces are skipped rather than pointing at nothing.
        const present = tourStepsFor(avatar).filter((s) => !s.target || findTarget(s.target));
        if (present.length === 0)
            return;
        setIndex(0);
        setSteps(present);
    }, [avatar]);
    // Return the panel to its main view first (so the My Projects / Demo Projects /
    // Create Project stops exist even if the user is inside a project), wait for
    // those sections to mount, then filter + show. Falls back after a few frames so
    // it can't hang.
    const beginTour = useCallback(() => {
        eventBus.emit('controlPanel:switchToMain');
        let attempts = 0;
        const tryStart = () => {
            attempts += 1;
            if (findTarget('my-projects') || findTarget('demo-projects') || attempts > 30) {
                startTour();
            }
            else {
                requestAnimationFrame(tryStart);
            }
        };
        requestAnimationFrame(tryStart);
    }, [startTour]);
    // Auto-start gate: armed (first-time signed-in user) AND the opening fly is done.
    const tryAutoStart = useCallback(() => {
        if (!armed.current || started.current || !isInitialFlyComplete())
            return;
        started.current = true;
        beginTour();
    }, [beginTour]);
    // Manual replay from Help & Support — runs immediately, regardless of gates.
    useEventSubscribe('onboarding:startTour', () => beginTour());
    // The fly may finish after we arm; pick it up here.
    useEventSubscribe('camera:initialFlyComplete', () => tryAutoStart());
    // Evaluate once per session: arm only for a signed-in, first-time user.
    // Hold off while the work and team step is still owed (every new account)
    // so that modal runs first; this effect re-fires and arms the tour once
    // needsUsagePurpose clears, by which time the avatar is known.
    useEffect(() => {
        if (evaluated.current)
            return;
        if (isLoading || !isAuthenticated || !(user === null || user === void 0 ? void 0 : user.userId))
            return;
        if (user.needsUsagePurpose)
            return;
        evaluated.current = true;
        if (hasSeenTour(user.userId))
            return;
        armed.current = true;
        tryAutoStart(); // in case the fly already completed before they logged in
    }, [isAuthenticated, isLoading, user === null || user === void 0 ? void 0 : user.userId, user === null || user === void 0 ? void 0 : user.needsUsagePurpose, tryAutoStart]);
    const active = steps.length > 0;
    const current = active ? steps[index] : null;
    // Track the target's rect; recompute on step change, resize, and scroll so the
    // highlight stays glued even when the layer list scrolls.
    useEffect(() => {
        if (!current) {
            setRect(null);
            return;
        }
        const update = () => {
            const el = findTarget(current.target);
            setRect(el ? el.getBoundingClientRect() : null);
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [current]);
    const finish = useCallback(() => {
        if (user === null || user === void 0 ? void 0 : user.userId)
            markTourSeen(user.userId);
        setSteps([]);
        setIndex(0);
        eventBus.emit('onboarding:tourFinished');
    }, [user === null || user === void 0 ? void 0 : user.userId]);
    if (!current)
        return null;
    const isFirst = index === 0;
    const isLast = index === steps.length - 1;
    // Bubble placement: prefer right of the target, then below, then above; for
    // centered steps (no rect) sit in the middle. Always clamp into the viewport.
    let bubbleLeft;
    let bubbleTop;
    if (!rect) {
        bubbleLeft = (window.innerWidth - BUBBLE_WIDTH) / 2;
        bubbleTop = (window.innerHeight - BUBBLE_EST_HEIGHT) / 2;
    }
    else if (rect.right + MARGIN + BUBBLE_WIDTH <= window.innerWidth) {
        bubbleLeft = rect.right + MARGIN;
        bubbleTop = rect.top;
    }
    else if (rect.bottom + MARGIN + BUBBLE_EST_HEIGHT <= window.innerHeight) {
        bubbleLeft = rect.left;
        bubbleTop = rect.bottom + MARGIN;
    }
    else {
        bubbleLeft = rect.left;
        bubbleTop = rect.top - MARGIN - BUBBLE_EST_HEIGHT;
    }
    bubbleLeft = Math.max(8, Math.min(bubbleLeft, window.innerWidth - BUBBLE_WIDTH - 8));
    bubbleTop = Math.max(8, Math.min(bubbleTop, window.innerHeight - BUBBLE_EST_HEIGHT - 8));
    return createPortal(_jsxs(_Fragment, { children: [_jsx("div", { style: {
                    position: 'fixed',
                    inset: 0,
                    zIndex: 1000,
                    backgroundColor: rect ? 'transparent' : 'rgba(0,0,0,0.55)',
                }, onClick: (e) => e.stopPropagation() }), rect && (_jsx("div", { style: {
                    position: 'fixed',
                    left: rect.left - HIGHLIGHT_PAD,
                    top: rect.top - HIGHLIGHT_PAD,
                    width: rect.width + HIGHLIGHT_PAD * 2,
                    height: rect.height + HIGHLIGHT_PAD * 2,
                    borderRadius: 8,
                    border: '2px solid var(--fern)',
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                    pointerEvents: 'none',
                    zIndex: 1001,
                } })), _jsxs("div", { style: {
                    position: 'fixed',
                    left: bubbleLeft,
                    top: bubbleTop,
                    width: BUBBLE_WIDTH,
                    zIndex: 1002,
                    backgroundColor: 'var(--forest)',
                    border: '1px solid var(--pewter)',
                }, className: "rounded-lg shadow-2xl p-4", children: [_jsxs("div", { className: "flex items-start justify-between gap-2 mb-2", children: [_jsx("h3", { className: "text-sm font-semibold", style: { color: 'var(--sand)' }, children: current.title }), _jsx("button", { onClick: finish, className: "flex-shrink-0 -mt-1 -mr-1 p-1 rounded hover:bg-gray-700/60 transition-colors", title: "Skip tour", "aria-label": "Skip tour", children: _jsx(X, { size: 15, style: { color: 'var(--dew)' } }) })] }), _jsx("p", { className: "text-xs leading-relaxed mb-4", style: { color: 'var(--dew)' }, children: current.body }), _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("span", { className: "text-[11px]", style: { color: 'var(--dew)' }, children: [index + 1, " of ", steps.length] }), _jsxs("div", { className: "flex items-center gap-2", children: [!isFirst && (_jsx("button", { onClick: () => setIndex((i) => Math.max(0, i - 1)), className: "px-3 py-1.5 text-xs rounded transition-colors", style: { backgroundColor: 'var(--charcoal)', color: 'var(--dew)' }, children: "Back" })), _jsx("button", { onClick: () => (isLast ? finish() : setIndex((i) => i + 1)), className: "px-3 py-1.5 text-xs font-semibold rounded transition-colors", style: { backgroundColor: 'var(--fern)', color: 'var(--sand)' }, children: isLast ? 'Done' : 'Next' })] })] })] })] }), document.body);
};
